import { useState } from "react";
import PropTypes from "prop-types";
import { Button, Progress } from "antd";
import { EnvironmentOutlined, FieldTimeOutlined, SwapOutlined } from "@ant-design/icons";
import CarSwapModal from "../../modals/CarSwap/CarSwapModal";
import ExtendReservationModal from "../../modals/ExtendReservation/ExtendReservationModal";
import {
    ACTION_SIZE_SM,
    SOFT_ACTION,
    formatScheduleLabel,
    getLocationLabel,
    getReservationDurationLabel,
    getReservationProgress,
    getVehicleSpecs,
} from "../utils";

const VehicleCard = ({ resDetails, carDetails, setResDetails, onCarSwapSuccess, onReservationExtended, t }) => {
    const [openSwapModal, setOpenSwapModal] = useState(false);
    const [openExtendModal, setOpenExtendModal] = useState(false);

    const carName =
        carDetails?.carName ||
        resDetails?.carSelected?.name ||
        resDetails?.carSelected?.carName ||
        t("carNotAssigned");
    const imageSrc = carDetails?.images?.image1 || carDetails?.images?.image2 || carDetails?.images?.image3;
    const specs = getVehicleSpecs(carDetails, resDetails);
    const pickupLocation = getLocationLabel(resDetails?.pickupLocation, t("notAvailable"));
    const dropoffLocation = getLocationLabel(
        resDetails?.dropoffLocation || resDetails?.pickupLocation,
        t("upcomingDetails.sameLocation"),
    );
    const durationLabel = getReservationDurationLabel(resDetails?.pickupDate, resDetails?.dropoffDate, t);
    const progress = getReservationProgress(resDetails?.pickupDate, resDetails?.dropoffDate);
    const canSwapCar = Boolean(
        resDetails?.isCarSelected &&
            !resDetails?.isCarPickedUp &&
            !resDetails?.isCancelled &&
            !resDetails?.isRejected,
    );
    // Upcoming or ongoing, with a car to price the extension against. `flag` is false for past
    // reservations, which live in a different collection and cannot be extended.
    const canExtend = Boolean(
        resDetails?.flag !== false &&
            resDetails?.carSelected &&
            !resDetails?.isCarDroppedOff &&
            !resDetails?.isCancelled &&
            !resDetails?.isRejected,
    );

    return (
        <>
            <div className="overflow-hidden rounded-2xl border border-[#f0f0f0] bg-white">
                <div className="flex flex-col lg:flex-row">
                    <div className="relative h-52 shrink-0 bg-[#f2f2fa] lg:h-auto lg:w-[280px]">
                        {imageSrc ? (
                            <img src={imageSrc} alt={carName} className="h-full w-full object-cover" />
                        ) : (
                            <div className="flex h-full min-h-[208px] items-center justify-center text-sm text-[#9b9ba5]">
                                {t("carNotAssigned")}
                            </div>
                        )}
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col gap-4 p-4 lg:p-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                                <p className="fontRegular text-xs uppercase tracking-[0.08em] text-[#9b9ba5]">
                                    {t("upcomingDetails.vehicle")}
                                </p>
                                <h2 className="fontSemi mt-1 text-lg text-[#2F2F46]">{carName}</h2>
                                {specs ? <p className="fontRegular mt-1 text-sm text-[#7f8c8d]">{specs}</p> : null}
                            </div>
                            {canSwapCar || canExtend ? (
                                // Equal-width row under the name on phones, compact pair top-right otherwise.
                                <div className="grid grid-flow-col auto-cols-fr gap-2 sm:flex sm:shrink-0">
                                    {canExtend ? (
                                        <Button
                                            icon={<FieldTimeOutlined />}
                                            onClick={() => setOpenExtendModal(true)}
                                            className={`${ACTION_SIZE_SM} ${SOFT_ACTION} w-full sm:w-auto`}>
                                            {t("extendReservation.button")}
                                        </Button>
                                    ) : null}
                                    {canSwapCar ? (
                                        <Button
                                            icon={<SwapOutlined />}
                                            onClick={() => setOpenSwapModal(true)}
                                            className={`${ACTION_SIZE_SM} ${SOFT_ACTION} w-full sm:w-auto`}>
                                            {t("carSwapModal.swapCar")}
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>

                        <div className="flex flex-col gap-3">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                                <div className="min-w-0 sm:flex-1">
                                    <p className="fontSemi text-sm text-[#2F2F46]">
                                        {formatScheduleLabel(resDetails?.pickupDate)}
                                    </p>
                                    <p className="fontRegular mt-1 flex items-center gap-1 text-sm text-[#7f8c8d]">
                                        <EnvironmentOutlined className="shrink-0 text-xs" />
                                        <span className="truncate">{pickupLocation}</span>
                                    </p>
                                </div>

                                <div className="min-w-0 sm:flex-1 sm:text-right">
                                    <p className="fontSemi text-sm text-[#2F2F46]">
                                        {formatScheduleLabel(resDetails?.dropoffDate)}
                                    </p>
                                    <p className="fontRegular mt-1 flex items-center justify-end gap-1 text-sm text-[#7f8c8d]">
                                        <EnvironmentOutlined className="shrink-0 text-xs" />
                                        <span className="truncate">{dropoffLocation}</span>
                                    </p>
                                </div>
                            </div>

                            <Progress
                                percent={Math.round(progress)}
                                showInfo={false}
                                strokeColor="#5D5DF6"
                                trailColor="#ECECF3"
                                strokeLinecap="round"
                                size={["100%", 6]}
                            />

                            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-xs">
                                <span className="fontRegular uppercase tracking-[0.08em] text-[#9b9ba5]">
                                    {t("upcomingDetails.pickup")}
                                </span>
                                <span className="fontSemi whitespace-nowrap text-center text-sm text-[#5D5DF6]">
                                    {durationLabel}
                                </span>
                                <span className="fontRegular text-right uppercase tracking-[0.08em] text-[#9b9ba5]">
                                    {t("upcomingDetails.dropoff")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CarSwapModal
                isOpen={openSwapModal}
                setIsOpen={setOpenSwapModal}
                resDetails={resDetails}
                onCarSwapSuccess={onCarSwapSuccess}
                setResDetails={setResDetails}
            />

            {canExtend ? (
                <ExtendReservationModal
                    isOpen={openExtendModal}
                    setIsOpen={setOpenExtendModal}
                    resDetails={resDetails}
                    onExtended={onReservationExtended}
                />
            ) : null}
        </>
    );
};

VehicleCard.propTypes = {
    resDetails: PropTypes.object,
    carDetails: PropTypes.object,
    setResDetails: PropTypes.func,
    onCarSwapSuccess: PropTypes.func,
    onReservationExtended: PropTypes.func,
    t: PropTypes.func.isRequired,
};

export default VehicleCard;
